import { withUkEnglish } from '../constants/llmInstructions';
import { htmlToPlainText } from './commonUtils';

/**
 * Short focus line so retrieval + synthesis prioritise what the diligence question asks.
 */
export function inferDiligenceQuestionFocus(questionText) {
    const q = String(questionText || '').trim();
    if (!q) return null;
    const lower = q.toLowerCase();

    if (/\bhow many\b/.test(lower) && /\b(staff|employee|fte|headcount|people|resource)\b/.test(lower)) {
        return 'Primary focus: IT staffing / headcount and who can support integration (names, roles, FTE, org chart). Do not lead with unrelated tooling unless staffing cannot be answered from evidence.';
    }
    if (/\bhow many\b/.test(lower)) {
        return 'Primary focus: quantities and counts asked in the diligence question. Answer that count first before peripheral topics.';
    }
    if (/\bwho\b/.test(lower) && /\b(integration|m&a|transaction)\b/.test(lower)) {
        return 'Primary focus: named people / roles who support integration — not generic IT stack unless people cannot be established.';
    }
    if (/\blist\b/.test(lower) && /\b(account|user|staff|employee)\b/.test(lower)) {
        return 'Primary focus: the list the question requests (accounts, staff, users). Tooling is secondary context only.';
    }
    if (/\bwhat do you use\b/.test(lower) || /\bwhich\b.*\b(tool|system|platform)\b/.test(lower)) {
        return 'Primary focus: tools/systems named in the question. Staffing topics are out of scope unless directly asked.';
    }
    return 'Primary focus: answer exactly what the diligence question asks first; treat other retrieved topics as secondary only.';
}

/** Terms to steer VDR semantic search toward the claim (names, roles, products). */
export function extractVdrSearchTerms(questionText, answerText) {
    const text = `${questionText || ''} ${answerText || ''}`.trim();
    if (!text) return [];

    const terms = new Set();
    const nameMatches = text.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+\b/g) || [];
    nameMatches.forEach(n => terms.add(n.trim()));

    const properTokens = text.match(/\b[A-Z][a-z]{2,}\b/g) || [];
    properTokens.forEach(t => {
        if (!/^(The|And|For|From|With|DevOps|Integration|Microsoft|Office|Business|Priority|Medium|High)$/i.test(t)) {
            terms.add(t);
        }
    });

    const tokens = text
        .replace(/[?.!,;:()]/g, ' ')
        .split(/\s+/)
        .map(t => t.trim())
        .filter(t => t.length > 2 && !/^(yes|no|the|and|for|are|is|was|were)$/i.test(t));

    for (let i = 0; i < tokens.length - 1; i += 1) {
        const pair = `${tokens[i]} ${tokens[i + 1]}`;
        if (/[A-Z]/.test(tokens[i]) || /developer|engineer|manager|director|lead/i.test(pair)) {
            terms.add(pair);
        }
    }
    tokens.forEach(t => {
        if (
            /developer|engineer|manager|architect|infrastructure|telephony|vmware|authenticator|billing|executech/i.test(
                t,
            )
        ) {
            terms.add(t);
        }
    });

    if (/\bhow many\b/i.test(questionText) && /\b(staff|employee|people)\b/i.test(questionText)) {
        terms.add('IT staff headcount');
        terms.add('employees FTE');
        terms.add('org chart');
        terms.add('integration resources');
    }
    if (/integration/i.test(text)) terms.add('M&A integration');
    if (/executech/i.test(text)) terms.add('Executech');
    if (/devops/i.test(text)) terms.add('DevOps');

    if (/service account/i.test(text)) terms.add('service account');
    if (/\b2FA\b/i.test(text)) terms.add('2FA MFA');
    if (/\bMFA\b/i.test(text)) terms.add('MFA');
    if (/\bO365\b/i.test(text)) terms.add('O365 Microsoft 365');

    const emails = text.match(/[\w.+-]+@[\w.-]+\.\w+/g) || [];
    emails.slice(0, 4).forEach(e => terms.add(e));

    return [...terms].slice(0, 10);
}

/**
 * Builds the intelligence ask question for IRL management-response evidence validation.
 * Centralized so components do not hardcode prompt wording.
 *
 * Wording avoids router keywords that force IRL-heavy retrieval (e.g. bare "IRL",
 * "information request", "response status") while keeping alignment/validation signals.
 */
/**
 * Pass 1 — VDR-only: answer the diligence question from documents in this workstream.
 */
export function buildVdrDiligencePassPrompt(irlRow, context = {}) {
    const { workstreamName = '', projectName = '' } = context;
    const questionText = htmlToPlainText(irlRow?.request || '').trim();
    const questionFocus = inferDiligenceQuestionFocus(questionText);
    const searchTerms = extractVdrSearchTerms(questionText, '');

    const lines = [
        'TASK: Search the Virtual Data Room (VDR) for this workstream and answer the diligence question below using documentary evidence only.',
        'VDR is the source of truth. Cite VDR chunks as [n] on every factual statement.',
        'Do not judge the management response in this step — only report what VDR documents establish.',
        projectName ? `Project: ${projectName}` : null,
        workstreamName ? `Workstream: ${workstreamName}` : null,
        questionFocus ? `Focus: ${questionFocus}` : null,
        searchTerms.length
            ? `Search hints: ${searchTerms.join('; ')}`
            : null,
        '',
        '--- DILIGENCE QUESTION ---',
        questionText || '(no question text)',
    ].filter(line => line != null);

    return withUkEnglish(lines.join('\n'));
}

/**
 * Pass 2 — Compare management response to VDR findings from pass 1.
 */
export function buildClaimValidationPassPrompt(irlRow, vdrPassResult, context = {}) {
    const base = buildIRLEvidenceValidationPrompt(irlRow, context);
    const vdrSummary = String(vdrPassResult?.final_answer || vdrPassResult?.answer || '').trim();
    if (!vdrSummary) return base;

    return withUkEnglish(
        [
            base,
            '',
            '--- VDR FINDINGS (PASS 1 — SOURCE OF TRUTH) ---',
            'Use these VDR-grounded findings when validating the management response. IRL rows are context only, not proof.',
            vdrSummary.slice(0, 12000),
        ].join('\n'),
    );
}

export function buildIRLEvidenceValidationPrompt(irlRow, context = {}) {
    const {
        workstreamName = '',
        projectName = '',
        irlRef = '',
    } = context;

    const ref =
        irlRef ||
        [irlRow?.prefix, irlRow?.item, irlRow?.sub_item].filter(v => v != null && v !== '').join('.') ||
        `DD-${irlRow?.id ?? 'unknown'}`;

    const questionText = htmlToPlainText(irlRow?.request || '').trim();
    const answerText = htmlToPlainText(irlRow?.irresponse?.answer || '').trim();
    const tag = irlRow?.tag ? String(irlRow.tag).trim() : '';
    const priority = irlRow?.priorities ? String(irlRow.priorities).trim() : '';

    const metadataLines = [
        projectName ? `Project: ${projectName}` : null,
        workstreamName ? `Workstream: ${workstreamName}` : null,
        tag ? `Tag / folder: ${tag}` : null,
        priority ? `Priority: ${priority}` : null,
        irlRow?.referenced_file
            ? `Referenced VDR document id (prioritise if relevant): ${irlRow.referenced_file}`
            : null,
    ].filter(Boolean);

    const searchTerms = extractVdrSearchTerms(questionText, answerText);
    const questionFocus = inferDiligenceQuestionFocus(questionText);

    const body = [
        'TASK: Validate whether the management response below is supported by documentary evidence in the Virtual Data Room (VDR).',
        '',
        'CRITICAL:',
        `- Validate ONLY diligence item ${ref} (IRL id ${irlRow?.id ?? 'unknown'}).`,
        `- ${ref} is an internal IRL tracker code for consultants — it will NOT appear in VDR documents. Do NOT report failure because the reference string is missing from evidence blocks.`,
        '- Judge support using the diligence question and management answer text against VDR content (systems, policies, contracts, org data, etc.).',
        '- Do not summarise unrelated information requests or other IRL references.',
        '- Substantiate or refute the management answer using VDR document content first; cite VDR chunks for role/title/fact checks.',
        '- Never cite this same diligence item (or its management answer) as evidence — that is circular.',
        '- The diligence question and management response below ARE the full instruction. Do NOT ask the user to clarify, specify, or rephrase — produce a validation verdict from retrieved VDR evidence.',
        '',
        'Required output — use these exact markdown section headings (in this order):',
        '## VDR Evidence Verdict',
        '  State exactly one: Supported / Partially supported / Not supported / Contradicted / Not corroborated by VDR.',
        '  Cite VDR document blocks [n] only — never cite IRL blocks as proof of the management answer.',
        '## VDR Documentary Evidence',
        '  Summarise supporting or contradicting VDR material with [n], or state none retrieved.',
        '## Related IRL Context — Not Proof',
        '  Other IRL items for cross-reference only; explicitly state they do not validate the claim.',
        '## Gaps / Missing Evidence',
        '  Where management omits or conflicts with VDR; bullets with VDR [n] where applicable.',
        '## Follow-up Questions',
        '  Consultant-ready diligence follow-ups when VDR proof is missing or weak.',
        'Optional: ## Evidence-based answer to the diligence question — answer --- DILIGENCE QUESTION --- from VDR [n] only;',
        '  Confidence: High|Medium|Low; or exactly "Not answerable from retrieved VDR evidence."',
        '',
        'The user question is ALWAYS the --- DILIGENCE QUESTION --- section below (not a separate message). Never state that no user question was provided.',
        ...(questionFocus ? ['', `QUESTION FOCUS: ${questionFocus}`, ''] : []),
        'Priority order:',
        '1. Address the diligence question directly (staff count, names, integration support, etc.) using VDR then management-claim checks.',
        '2. Search in-scope VDR documents (org charts, HR lists, contracts, policies, spreadsheets) for material on that specific question.',
        '3. Only after the primary question is addressed (or marked not answerable), add secondary context (e.g. tools, monitoring) if present in evidence.',
        '4. Use other management responses only as cross-reference — never replace the primary answer with a different IRL topic.',
        '',
        ...(searchTerms.length
            ? ['VDR search focus (names, roles, topics from this claim):', ...searchTerms.map(t => `- ${t}`), '']
            : []),
        'Deliver consultant-grade evidence validation (not open-ended chat):',
        '- Supporting documentary (VDR) evidence with citations',
        '- Contradictions between the management position and VDR material',
        '- Missing or weak documentary proof and material gaps',
        '- Recommended follow-up diligence questions',
        '',
        'Validate alignment between the management position and VDR evidence. Ground claims in retrieved evidence only.',
        '',
        `Internal tracker code (not a VDR filename — do not search the data room for this string): ${ref}`,
        ...metadataLines,
        '',
        '--- DILIGENCE QUESTION ---',
        questionText || '(no question text)',
        '',
        '--- MANAGEMENT RESPONSE ---',
        answerText || '(no response text)',
    ].join('\n');

    return withUkEnglish(body);
}

function coerceWorkstreamId(irlRow) {
    const raw = irlRow?.workstream ?? irlRow?.workstream_id;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/**
 * Scope for buildIntelligenceAskBody.
 *
 * When the IRL row has a linked VDR file (`referenced_file`), search that document explicitly —
 * consultants attach the file that should support the claim.
 * Otherwise search the full permitted workstream (do not pass file_ids).
 */
export function buildIRLEvidenceAskScope(irlRow) {
    const wsId = coerceWorkstreamId(irlRow);
    const workstreamIds = wsId != null ? [wsId] : [];
    const irlId = Number(irlRow?.id);
    const refFileId = Number(irlRow?.referenced_file);
    const fileIds =
        Number.isFinite(refFileId) && refFileId > 0 ? [refFileId] : [];

    return {
        workstreamIds,
        fileIds,
        folderIds: [],
        wsId,
        irlId: Number.isFinite(irlId) ? irlId : null,
    };
}
